import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import { config } from '../../config';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { handleArchiveCard, pushArchivedCard } from '../../redux/Slices/cardSlice';
import { addMember, removeMember, addMemberOptimistic, removeMemberOptimistic, fetchMembers } from '../../redux/Slices/memberSlice';
import { addActivityOptimistically } from '../../redux/Slices/activitiesSlice';
import './scss/CardActions.scss';
import NotificationsActive from '../icons/NotificationsActive';
import Notifications from '../icons/Notifications';
import { useBoard } from '../../context/BoardContext';
import {
    PersonAdd,
    PersonRemove,
    Edit,
    ContentCopy,
    DriveFileMove,
    Share,
    Archive,
    Unarchive,
    Delete
} from '../icons';

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
    'person_add': PersonAdd,
    'person_remove': PersonRemove,
    'edit': Edit,
    'content_copy': ContentCopy,
    'drive_file_move': DriveFileMove,
    'share': Share,
    'notifications': Notifications,
    'notifications_active': NotificationsActive,
    'archive': Archive,
    'unarchive': Unarchive,
    'delete': Delete,
};

const CardActions = ({ cardId, card, isFromCard = false, isReadOnly = false }) => {
    const { handlePopoverClick } = useHandlePopoverClick();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const [error, setError] = useState(null);
    const [isLeaving, setIsLeaving] = useState(false);

    const userId = user?.user?._id;

    const { boardState, setBoard, setEditCard } = useBoard();

    const [isWatching, setIsWatching] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isHidden, setIsHidden] = useState(false);

    // Local optimistic state for UI updates
    const [optimisticArchived, setOptimisticArchived] = useState(card?.archived || false);
    const [optimisticIsMember, setOptimisticIsMember] = useState(false);

    // Use optimistic state for isUserMember
    const isUserMember = optimisticIsMember;

    // Reset all states when cardId changes (when opening a different card's menu)
    useEffect(() => {
        setIsLeaving(false);
        setIsJoining(false);
        setIsArchiving(false);
        setIsHidden(false);
        setError(null);
        setOptimisticArchived(card?.archived || false);

        // Calculate initial member state from card data
        const isMember = card?.users?.some(member => {
            const memberId = member?._id || member;
            return memberId === userId;
        }) || false;
        setOptimisticIsMember(isMember);
    }, [cardId, card?.archived, card?.users, userId]);

    // Check if user is watching the card - recalculate when card changes
    useEffect(() => {
        if (card?.watchers && userId) {
            const userIsWatching = card.watchers.some(watcher => {
                const watcherId = watcher?._id || watcher;
                return watcherId === userId;
            });
            setIsWatching(userIsWatching);
        } else {
            setIsWatching(false);
        }
    }, [card?.watchers, userId, cardId]);

    const handleToggleMemberToCard = async (e, member, add = true) => {
        e.preventDefault();

        const memberId = member?.user?._id || member._id;
        const memberData = member?.user || member;
        if (!memberId) return console.error("Invalid member data");

        const setLoading = add ? setIsJoining : setIsLeaving;
        setLoading(true);

        // Optimistic update - update local state immediately
        setOptimisticIsMember(add);

        // Optimistic update - update board state immediately
        setBoard(prev => ({
            ...prev,
            actionLists: prev.actionLists.map(al => ({
                ...al,
                cards: al.cards.map(card =>
                    typeof card === "string" || !card.shortId
                        ? card
                        : card.shortId === cardId
                            ? {
                                ...card,
                                users: add
                                    ? [...card.users, memberData]
                                    : card.users.filter(u => u._id !== memberId),
                            }
                            : card
                ),
            })),
        }));

        try {
            await dispatch(add ? addMember({ cardId, memberId }) : removeMember({ cardId, memberId })).unwrap();

            // Add optimistic activity if details are enabled
            if (card?.isActivityDetails && user?.user) {
                const isSelf = userId === memberId;
                const actionType = add 
                    ? (isSelf ? 'JOINED' : 'ADDED_MEMBER') 
                    : (isSelf ? 'LEFT' : 'REMOVED_MEMBER');
                const details = add 
                    ? (isSelf ? 'joined this card.' : `added ${memberData.name} to this card.`) 
                    : (isSelf ? 'left this card.' : `removed ${memberData.name} from this card.`);

                dispatch(addActivityOptimistically({
                    cardId,
                    userId: userId,
                    actionType,
                    details,
                    user: user.user
                }));
            }

            // Update Redux state after success
            dispatch(add ? addMemberOptimistic({ cardId, member: memberData }) : removeMemberOptimistic({ cardId, memberId }));

        } catch (error) {
            // Rollback optimistic state on error
            setOptimisticIsMember(!add);

            // Rollback board state
            setBoard(prev => ({
                ...prev,
                actionLists: prev.actionLists.map(al => ({
                    ...al,
                    cards: al.cards.map(card =>
                        typeof card === "string" || !card.shortId
                            ? card
                            : card.shortId === cardId
                                ? {
                                    ...card,
                                    users: add
                                        ? card.users.filter(u => u._id !== memberId)
                                        : [...card.users, memberData],
                                }
                                : card
                    ),
                })),
            }));
            setError(`Failed to ${add ? "add" : "remove"} member.`);
        } finally {
            setLoading(false);
        }
    };

    const handleWatch = async () => {
        const previousWatchingState = isWatching;

        try {
            setIsWatching(!isWatching);

            showSuccess(dispatch, isWatching ? 'Stopped watching card' : 'Now watching card');

            if (!userId) return;

            const endpoint = previousWatchingState ? 'unwatch' : 'watch';

            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ cardId }),
            });

            if (!response.ok) throw new Error(`Failed to ${endpoint} the card`);

            setBoard(prev => ({
                ...prev,
                actionLists: prev.actionLists.map(al => ({
                    ...al,
                    cards: al.cards.map(card =>
                        typeof card === "string" || !card.shortId
                            ? card
                            : card.shortId === cardId
                                ? {
                                    ...card,
                                    watchers: previousWatchingState
                                        ? card.watchers.filter(w => {
                                            const watcherId = w?._id || w;
                                            return watcherId !== userId;
                                        })
                                        : [...card.watchers, userId], // Add just the userId, not an object
                                }
                                : card
                    ),
                })),
            }));

        } catch (error) {
            setIsWatching(previousWatchingState);
            showError(dispatch, error.message);
        }
    };



    const handleArchive = async () => {
        if (isArchiving) return;
        setIsArchiving(true);

        const previousArchivedState = optimisticArchived;

        try {
            // Optimistic UI update - immediately change button state
            setOptimisticArchived(!optimisticArchived);

            // Optimistically add to archive list if status is true
            if (!previousArchivedState) {
                dispatch(pushArchivedCard({ ...card, archived: true }));
            }

            if (isFromCard) {
                dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
            }

            await dispatch(handleArchiveCard({
                archivedStatus: !previousArchivedState,
                cardId
            })).unwrap();

            // Add optimistic activity if details are enabled
            if (card?.isActivityDetails && user?.user) {
                dispatch(addActivityOptimistically({
                    cardId,
                    userId: userId,
                    actionType: 'ARCHIVED',
                    details: !previousArchivedState ? "archived this card." : "unarchived this card.",
                    user: user.user
                }));
            }

            setBoard(prev => ({
                ...prev,
                actionLists: prev.actionLists.map(al => ({
                    ...al,
                    cards: al.cards.map(c => {
                        // If unarchiving, add the restoration mark
                        if (c.shortId === cardId && previousArchivedState) {
                            return { ...c, isRecentlyUnarchived: true };
                        }
                        return c;
                    }).filter(c =>
                        typeof c === "string" || !c.shortId
                            ? true
                            : !(c.shortId === cardId && !previousArchivedState) // Remove if archiving
                    ),
                })),
            }));

            showSuccess(dispatch, optimisticArchived ? 'Card unarchived' : 'Card archived');


        } catch (error) {
            // Revert optimistic update on error
            setOptimisticArchived(previousArchivedState);
            showError(dispatch, 'Failed to archive card');
        } finally {
            setIsArchiving(false);
        }
    };

    const handleHideFromList = () => {
        // Optimistic update
        setIsHidden(!isHidden);
        showSuccess(dispatch, isHidden ? 'Card shown in list' : 'Card hidden from list');
        // Close popover after hiding
        dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
    };

    const handleDelete = (e) => {
        handlePopoverClick(e, 'deleteCard', {
            isBack: true,
            backPopover: 'cardActions',
            cardId,
            card,
            isPopPositionChanged: false,

        });
    };
    const cardActions = [
        {
            id: 'popover-card-back-toggle-member-button',
            title: isUserMember ? 'Leave' : 'Join',
            icon: isUserMember ? 'person_remove' : 'person_add',
            description: isUserMember ? 'Leave this card' : 'Join this card',
            onClick: (e) => handleToggleMemberToCard(e, user, !isUserMember),
            loading: isJoining || isLeaving,
            disabled: isJoining || isLeaving,
            isVisible: !isReadOnly,
            loadingText: isJoining
                ? 'Joining'
                : isLeaving
                    ? 'Leaving'
                    : isUserMember
                        ? 'Leave'
                        : 'Join',

        },
        {
            id: 'popover-card-back-edit-card-button',
            title: 'Edit Card',
            icon: 'edit',
            description: 'Edit this card',
            isVisible: !isReadOnly,
            onClick: (e) => {
                setEditCard(cardId);
                dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
            }
        },
        {
            id: 'popover-card-back-copy-card-button',
            title: 'Copy Card',
            icon: 'content_copy',
            description: 'Duplicate this card to another use',
            isVisible: !isReadOnly,
            onClick: (e) => handlePopoverClick(e, 'copyCard', {
                isBack: true,
                backPopover: 'cardActions',
                cardId,
                card,
                isPopPositionChanged: false
            })
        },
        {
            id: 'popover-card-back-move-card-button',
            title: 'Move',
            icon: 'drive_file_move',
            description: 'Move this card to another list',
            isVisible: !isReadOnly,
            onClick: (e) => handlePopoverClick(e, 'moveCard', {
                isBack: true,
                backPopover: 'cardActions',
                cardId,
                card,
                isPopPositionChanged: false
            })
        },
        {
            id: 'popover-card-back-share-button',
            title: 'Share',
            icon: 'share',
            description: 'Share this card',
            isVisible: true,
            onClick: (e) => handlePopoverClick(e, 'shareCard', {
                isBack: true,
                backPopover: 'cardActions',
                cardId,
                card,
                isPopPositionChanged: false
            })
        },
        {
            id: 'popover-card-back-watch-button',
            title: isWatching ? 'Notifying' : 'Notify',
            icon: isWatching ? 'notifications_active' : 'notifications',
            description: isWatching ? 'Stop getting updates' : 'Get updates about this card',
            isVisible: true,
            onClick: handleWatch
        },

        {
            id: optimisticArchived ? 'popover-card-back-send-to-board-button' : 'popover-card-back-archive-button',
            title: optimisticArchived ? 'Send to board' : 'Archive',
            icon: optimisticArchived ? 'unarchive' : 'archive',
            description: optimisticArchived ? 'Restore this card' : 'Archive this card',
            isVisible: !isReadOnly,
            onClick: handleArchive,
            loading: isArchiving,
            disabled: isArchiving,
            className: 'archive-action',
            loadingText: isArchiving
                ? (optimisticArchived ? 'Unarchiving' : 'Archiving')
                : ''
        },
        ...(optimisticArchived ? [{
            id: 'popover-card-back-delete-card-button',
            title: 'Delete',
            icon: 'delete',
            description: 'Permanently delete this card',
            isVisible: !isReadOnly,
            onClick: handleDelete,
            className: 'delete-action'
        }] : [])
    ];


    return (
        <div className="card-actions-popover">
            <div className="popover-header">
                <h3>Actions</h3>
            </div>
            <div className="popover-content">
                {cardActions.map((action) => (
                    action.isVisible && (
                        <button
                            key={action.id}
                            className={`action-item ${action.className || ''} ${action.disabled ? 'full-deactivated' : ''}`}
                            onClick={action.onClick}
                            disabled={action.disabled}
                            data-popover-trigger
                            id={action.id}
                        >
                            {(() => {
                                const IconComponent = ICON_MAP[action.icon];
                                return IconComponent ? <IconComponent size={20} /> : null;
                            })()}
                            <div className="action-info">
                                <span className="action-title">
                                    {action.loading && action.loadingText ? action.loadingText : action.title}
                                </span>
                                {action.description && (
                                    <span className="action-description">{action.description}</span>
                                )}
                            </div>
                        </button>
                    )
                ))}
            </div>

        </div>
    );
};

export default CardActions;
